import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarDays, Clock, MapPin, Sparkles } from "lucide-react";

const HERO_URL = "/images/piano-hero.jpg";

const LINES = ["Masterclass", "Piano Gospel", "Gratuite"];

const lineVariants = {
  hidden: { y: "110%", opacity: 0 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    transition: { delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[94vh] flex items-end overflow-hidden grain"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_URL} alt="Cours collectif de piano gospel au conservatoire" className="w-full h-full object-cover object-[50%_35%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C1E16] via-[#2C1E16]/55 to-[#2C1E16]/25" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-overline"
          className="flex items-center gap-2 text-[#F0A202] uppercase tracking-[0.25em] text-xs font-bold mb-6"
        >
          <Sparkles className="w-4 h-4" />
          CMC-Belgique — Conservatoire de la Musique Chrétienne
        </motion.p>

        <h1 className="font-display font-black tracking-tight leading-[0.95] text-[#FDFBF7] text-4xl sm:text-5xl lg:text-6xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "italic text-[#F0A202]" : ""}`}
                data-testid={`hero-line-${i}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-[#D4BAA3] text-base md:text-lg"
        >
          Accords gospel, accompagnement, improvisation — enrichissez votre jeu au piano lors
          d'une soirée offerte par le Conservatoire de la Musique Chrétienne, ouverte à tous les niveaux.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-x-10 gap-y-4 text-[#FFF4ED]"
        >
          <span className="flex items-center gap-3" data-testid="hero-date">
            <CalendarDays className="w-5 h-5 text-[#D95D39]" />
            <span className="text-sm md:text-base font-semibold">Samedi 10 octobre</span>
          </span>
          <span className="flex items-center gap-3" data-testid="hero-time">
            <Clock className="w-5 h-5 text-[#D95D39]" />
            <span className="text-sm md:text-base font-semibold">19h00</span>
          </span>
          <span className="flex items-center gap-3" data-testid="hero-location">
            <MapPin className="w-5 h-5 text-[#D95D39]" />
            <span className="text-sm md:text-base font-semibold">
              81 Tollaan, 1200 Woluwe-Saint-Lambert — Bruxelles
            </span>
          </span>
        </motion.div>
      </div>
    </section>
  );
}
